import styled from 'styled-components';
import {tokensForThemeProvider} from '@forecast-it/design-system';

const {color, spacing, size} = tokensForThemeProvider.sky.sys;

const BORDER = `${size.borderwidth.thin} solid ${color.border.decorative}`;

// Chevron, company, and a right-aligned count, shared by the column bar and every company row.
const ROW_COLUMNS = `44px minmax(0, 1fr) 160px`;

/** One bordered table holding the title, the column bar and every company row. */
export const CompaniesPanel = styled.div`
	border: ${BORDER};
	border-radius: ${size.borderradius['3']};
	overflow: hidden;
	background: ${color.background.neutral['1']};
`;

export const PanelHeader = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacing['100']};
	padding: ${spacing['400']} ${spacing['600']};
	border-bottom: ${BORDER};
`;

export const ColumnBar = styled.div`
	display: grid;
	grid-template-columns: ${ROW_COLUMNS};
	align-items: center;
	height: 40px;
	padding: 0 ${spacing['600']} 0 ${spacing['300']};
	background: ${color.background.neutral['2']};
	border-bottom: ${BORDER};
`;

export const CompanyItem = styled.div`
	&:not(:last-child) {
		border-bottom: ${BORDER};
	}
`;

/** The clickable company line; tinted while its guests are showing. */
export const CompanyRowHeader = styled.div<{open: boolean}>`
	display: grid;
	grid-template-columns: ${ROW_COLUMNS};
	align-items: center;
	padding: ${spacing['300']} ${spacing['600']} ${spacing['300']} ${spacing['300']};
	cursor: pointer;
	background: ${props => (props.open ? color.background.neutral['2'] : color.background.neutral['1'])};

	&:hover {
		background: ${color.background.neutral['2']};
	}

	&:focus-visible {
		outline: ${size.borderwidth.thick} solid ${color.border.focusring};
		outline-offset: -${size.borderwidth.thick};
	}
`;

export const CenteredCell = styled.span`
	display: flex;
	justify-content: center;
`;

export const RightCell = styled.span`
	display: flex;
	justify-content: flex-end;
`;

/** Indents the guest table under the company name, past the chevron column. */
export const GuestPanel = styled.div`
	padding: 0 ${spacing['600']} ${spacing['400']} calc(${spacing['800']} + ${spacing['600']});
	background: ${color.background.neutral['2']};
`;

/**
 * The tooltip's text. A name or email can be one long run with no spaces, which would otherwise run
 * out of the tooltip on a single line, so it breaks anywhere to stay inside the tooltip's width.
 */
export const FullValueText = styled.div`
	overflow-wrap: anywhere;
	/* The design-system tooltip turns text selection off for its content; this switches it back on so
	   the whole name or email can be selected and copied. */
	user-select: text;
`;

/** The hoverable text of a Guest or Client cell. Inline, so the cell's ellipsis still cuts it. */
export const HoverText = styled.span`
	cursor: default;
`;

/**
 * Keeps the guest table inside its box however wide a value is. The design-system table sizes columns
 * to their content and never wraps a cell, so one wide name or email would push the right-hand columns
 * out of view. Fixed layout holds each column to its set width, and every line of a two-line cell is
 * cut with an ellipsis instead.
 */
export const GuestBox = styled.div`
	border: ${BORDER};
	border-radius: ${size.borderradius['2']};
	overflow: hidden;
	background: ${color.background.neutral['1']};

	table {
		table-layout: fixed;
	}

	td,
	td div {
		min-width: 0;
	}

	td div {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
`;
